const APP_BASE = 'https://console.filemingle.com';

export const tools = [
  {
    slug: 'image-to-png',
    metaTitle: 'Free Image to PNG Converter Online | FileMingle',
    metaDescription:
      'Convert JPG, JPEG, WebP, and other image formats to PNG online with FileMingle. Fast, simple, and browser-based.',
    h1: 'Convert Images to PNG Online',
    badge: 'Image Converter',
    description:
      'Upload any image and instantly convert it to a high-quality PNG file — entirely in your browser. No software, no sign-up required.',
    trustMessage: 'Fast, simple, and browser-based.',
    appUrl: `${APP_BASE}/image-to-png`,
    primaryCta: 'Convert Image to PNG',
    benefits: [
      {
        icon: 'Globe',
        title: 'No Software Required',
        description: 'Run directly in your browser. Nothing to download or install.',
      },
      {
        icon: 'Layers',
        title: 'Multiple Formats Supported',
        description: 'Accepts JPG, JPEG, WebP, BMP, GIF, and more as input.',
      },
      {
        icon: 'ShieldCheck',
        title: 'Privacy First',
        description: 'Files are processed locally — never uploaded to external servers.',
      },
      {
        icon: 'Zap',
        title: 'Instant Results',
        description: 'Conversion happens in seconds, right in your tab.',
      },
      {
        icon: 'ImageIcon',
        title: 'Lossless PNG Output',
        description: 'Preserve full image quality with lossless PNG encoding.',
      },
      {
        icon: 'Sparkles',
        title: 'Free to Use',
        description: 'No hidden fees, no paywalls. Completely free.',
      },
    ],
    steps: [
      {
        step: 1,
        title: 'Upload Your Image',
        description: 'Drag and drop or click to select any image file from your device.',
      },
      {
        step: 2,
        title: 'Choose Settings',
        description: 'Pick your desired quality and output options.',
      },
      {
        step: 3,
        title: 'Download PNG',
        description: 'Your converted PNG file is ready to download instantly.',
      },
    ],
    featureDetails:
      'FileMingle\'s image-to-PNG converter supports a wide range of input formats including JPG, JPEG, WebP, BMP, and GIF. The conversion preserves your original image dimensions and quality. Because processing happens in your browser, your images never leave your device — making it ideal for sensitive or private images.',
    faqs: [
      {
        q: 'What image formats can I convert to PNG?',
        a: 'FileMingle supports JPG, JPEG, WebP, BMP, GIF, and several other common image formats as input for PNG conversion.',
      },
      {
        q: 'Is the image to PNG converter free?',
        a: 'Yes, converting images to PNG with FileMingle is completely free. No account or payment is required.',
      },
      {
        q: 'Do I need to install any software?',
        a: 'No installation needed. The converter runs entirely in your web browser on any device.',
      },
      {
        q: 'Are my images stored after conversion?',
        a: 'No. FileMingle processes images in your browser. Your files are never uploaded to or stored on our servers.',
      },
      {
        q: 'Can I use this converter on mobile?',
        a: 'Yes, FileMingle works on smartphones and tablets. Open it in any modern mobile browser.',
      },
      {
        q: 'Will converting to PNG reduce image quality?',
        a: 'No. PNG is a lossless format, so your converted image maintains the same quality as the original.',
      },
    ],
    relatedSlugs: ['jpg-to-png', 'compress-image', 'png-compressor'],
  },

  {
    slug: 'compress-image',
    metaTitle: 'Compress Image Online for Free | FileMingle',
    metaDescription:
      'Reduce image file size online while keeping quality. Compress JPG, PNG, and other images directly from your browser with FileMingle.',
    h1: 'Compress Images Online',
    badge: 'Image Compressor',
    description:
      'Reduce your image file sizes significantly while preserving visual quality — no software needed, right from your browser.',
    trustMessage: 'Reduce file size without sacrificing quality.',
    appUrl: `${APP_BASE}/compress-image`,
    primaryCta: 'Compress Image Now',
    benefits: [
      {
        icon: 'Minimize2',
        title: 'Shrink File Size',
        description: 'Reduce image sizes by up to 80% without visible quality loss.',
      },
      {
        icon: 'Layers',
        title: 'Supports JPG & PNG',
        description: 'Compress the most common image formats in one place.',
      },
      {
        icon: 'Sliders',
        title: 'Adjustable Quality',
        description: 'Control the compression level to find your ideal size-quality balance.',
      },
      {
        icon: 'ShieldCheck',
        title: 'Private & Secure',
        description: 'Images are compressed in your browser — never sent to a server.',
      },
      {
        icon: 'Zap',
        title: 'Instant Processing',
        description: 'Compression completes in seconds, even for large images.',
      },
      {
        icon: 'Globe',
        title: 'Works Everywhere',
        description: 'Available on any device with a modern browser — no installs.',
      },
    ],
    steps: [
      {
        step: 1,
        title: 'Upload Your Image',
        description: 'Select one or more images to compress from your device.',
      },
      {
        step: 2,
        title: 'Set Compression Level',
        description: 'Choose how aggressively to compress — balance size vs quality.',
      },
      {
        step: 3,
        title: 'Download Compressed File',
        description: 'Save your smaller image immediately — no waiting.',
      },
    ],
    featureDetails:
      'FileMingle\'s image compressor uses advanced compression algorithms to reduce file sizes while maintaining visual clarity. It supports JPG and PNG formats and lets you adjust quality from lossless to highly compressed. Ideal for optimizing images for websites, email attachments, or storage.',
    faqs: [
      {
        q: 'How much can I reduce image file size?',
        a: 'Depending on the original image and compression settings, you can often reduce file sizes by 50–80% without noticeable quality loss.',
      },
      {
        q: 'Is image compression free on FileMingle?',
        a: 'Yes, FileMingle\'s image compression tool is completely free to use.',
      },
      {
        q: 'What image formats can I compress?',
        a: 'FileMingle supports compressing JPG, JPEG, and PNG files.',
      },
      {
        q: 'Will compression ruin my image quality?',
        a: 'Not necessarily. You control the compression level. Light compression is virtually invisible, while heavy compression may show minor artifacts.',
      },
      {
        q: 'Are my images uploaded to your servers?',
        a: 'No. All compression happens locally in your browser. Your images never leave your device.',
      },
      {
        q: 'Can I compress multiple images at once?',
        a: 'Yes, FileMingle allows batch compression so you can process multiple images in one session.',
      },
    ],
    relatedSlugs: ['image-to-png', 'jpg-to-png', 'png-compressor'],
  },

  {
    slug: 'fillable-pdf-builder',
    metaTitle: 'Create Fillable PDF Online | FileMingle',
    metaDescription:
      'Turn any PDF into a fillable form by adding text fields, signature fields, date fields, checkboxes, and more — free online with FileMingle.',
    h1: 'Create Fillable PDFs Online',
    badge: 'PDF Tool',
    description:
      'Upload any PDF and transform it into a fully interactive fillable form. Add text fields, signatures, checkboxes, and date pickers — no software needed.',
    trustMessage: 'Turn any PDF into a fillable form in minutes.',
    appUrl: `${APP_BASE}/pdf-form-builder`,
    primaryCta: 'Create Fillable PDF',
    benefits: [
      {
        icon: 'FileText',
        title: 'Any PDF, Made Fillable',
        description: 'Upload any existing PDF and add interactive fields instantly.',
      },
      {
        icon: 'Edit3',
        title: 'Rich Field Types',
        description: 'Add text inputs, signatures, date pickers, checkboxes, and dropdowns.',
      },
      {
        icon: 'MousePointerClick',
        title: 'Drag & Drop Builder',
        description: 'Place fields anywhere on the document with intuitive drag-and-drop.',
      },
      {
        icon: 'Download',
        title: 'Instant PDF Export',
        description: 'Download your fillable PDF ready for sharing or distribution.',
      },
      {
        icon: 'Globe',
        title: 'No Install Required',
        description: 'Works entirely in your browser — no Acrobat or plugins needed.',
      },
      {
        icon: 'Sparkles',
        title: 'Free to Use',
        description: 'Build fillable PDFs for free. No subscription required.',
      },
    ],
    steps: [
      {
        step: 1,
        title: 'Upload Your PDF',
        description: 'Import any PDF document you want to make fillable.',
      },
      {
        step: 2,
        title: 'Add Form Fields',
        description: 'Drag text boxes, signatures, checkboxes, and date fields onto the PDF.',
      },
      {
        step: 3,
        title: 'Download & Share',
        description: 'Export your fillable PDF and share it with anyone.',
      },
    ],
    featureDetails:
      'FileMingle\'s fillable PDF builder lets you transform any static PDF into an interactive form without Adobe Acrobat. Add text fields, signature blocks, date pickers, checkboxes, and dropdown menus. Position fields precisely using drag-and-drop. Export a standard PDF that anyone can fill out using any PDF viewer.',
    faqs: [
      {
        q: 'Do I need Adobe Acrobat to create fillable PDFs?',
        a: 'No. FileMingle works in your browser and doesn\'t require Adobe Acrobat or any other software.',
      },
      {
        q: 'What types of form fields can I add?',
        a: 'You can add text fields, signature fields, date pickers, checkboxes, radio buttons, and dropdown menus.',
      },
      {
        q: 'Can anyone fill out the PDF I create?',
        a: 'Yes. The exported PDF is a standard fillable PDF that anyone can open and fill in using Adobe Reader, Preview, or any modern PDF viewer.',
      },
      {
        q: 'Is creating fillable PDFs free?',
        a: 'Yes, FileMingle\'s fillable PDF builder is free to use.',
      },
      {
        q: 'Can I use this on a mobile device?',
        a: 'Yes. FileMingle is fully responsive and works on smartphones and tablets.',
      },
      {
        q: 'Is my PDF stored on your servers?',
        a: 'FileMingle prioritizes privacy. Documents are processed in your browser session and are not permanently stored on our servers.',
      },
    ],
    relatedSlugs: ['pdf-form-builder', 'image-to-png', 'compress-image'],
  },

  {
    slug: 'pdf-form-builder',
    metaTitle: 'PDF Form Builder Online | FileMingle',
    metaDescription:
      'Build professional PDF forms online with drag-and-drop fields including text, signatures, dates, checkboxes, and dropdowns — free with FileMingle.',
    h1: 'PDF Form Builder',
    badge: 'PDF Tool',
    description:
      'Design and build professional PDF forms with a drag-and-drop editor. Add any field type and export a ready-to-distribute fillable PDF.',
    trustMessage: 'Professional PDF forms — built in minutes.',
    appUrl: `${APP_BASE}/pdf-form-builder`,
    primaryCta: 'Launch PDF Form Builder',
    benefits: [
      {
        icon: 'LayoutTemplate',
        title: 'Drag-and-Drop Editor',
        description: 'Build forms visually by placing fields exactly where you need them.',
      },
      {
        icon: 'Edit3',
        title: 'All Field Types',
        description: 'Text, signature, date, checkbox, radio button, and dropdown fields.',
      },
      {
        icon: 'FileCheck',
        title: 'Professional Output',
        description: 'Export standard PDF forms compatible with all major PDF readers.',
      },
      {
        icon: 'Globe',
        title: 'Browser-Based',
        description: 'No downloads, no plugins. Works in any modern browser.',
      },
      {
        icon: 'Zap',
        title: 'Fast & Lightweight',
        description: 'Build and export forms quickly without heavy software.',
      },
      {
        icon: 'Sparkles',
        title: 'Free to Start',
        description: 'Create PDF forms for free with no hidden costs.',
      },
    ],
    steps: [
      {
        step: 1,
        title: 'Upload a PDF',
        description: 'Import an existing PDF to use as the form base.',
      },
      {
        step: 2,
        title: 'Build Your Form',
        description: 'Add and position form fields using the drag-and-drop editor.',
      },
      {
        step: 3,
        title: 'Export & Distribute',
        description: 'Download your finished PDF form and share it with recipients.',
      },
    ],
    featureDetails:
      'The FileMingle PDF Form Builder gives you a full-featured drag-and-drop interface for creating professional fillable forms from any PDF. Unlike desktop tools, everything runs in your browser — no Adobe Acrobat license needed. Export standard fillable PDFs that work with Adobe Reader, macOS Preview, and all common PDF viewers.',
    faqs: [
      {
        q: 'What is a PDF form builder?',
        a: 'A PDF form builder lets you take any PDF document and add interactive form fields — like text boxes, checkboxes, and signature lines — so recipients can fill it in digitally.',
      },
      {
        q: 'Do I need to install software to use the PDF form builder?',
        a: 'No. FileMingle\'s PDF form builder works entirely in your browser with no installation required.',
      },
      {
        q: 'What field types are supported?',
        a: 'Text fields, signature fields, date pickers, checkboxes, radio buttons, and dropdown menus.',
      },
      {
        q: 'Will the exported PDF work in Adobe Reader?',
        a: 'Yes. FileMingle exports standard fillable PDFs compatible with Adobe Reader, macOS Preview, and other PDF viewers.',
      },
      {
        q: 'Is the PDF form builder free?',
        a: 'Yes, you can build and export PDF forms for free.',
      },
      {
        q: 'Can I use it on mobile?',
        a: 'Yes. The PDF form builder is fully responsive and accessible from smartphones and tablets.',
      },
    ],
    relatedSlugs: ['fillable-pdf-builder', 'image-to-png', 'compress-image'],
  },

  {
    slug: 'jpg-to-png',
    metaTitle: 'JPG to PNG Converter Online | FileMingle',
    metaDescription:
      'Convert JPG and JPEG files to PNG online quickly with FileMingle. No installation required — fast, free, and browser-based.',
    h1: 'Convert JPG to PNG Online',
    badge: 'Image Converter',
    description:
      'Convert JPG and JPEG images to PNG format instantly in your browser. Preserve full quality with lossless PNG output — free and private.',
    trustMessage: 'Free JPG to PNG conversion in your browser.',
    appUrl: `${APP_BASE}/image-to-png`,
    primaryCta: 'Convert JPG to PNG',
    benefits: [
      {
        icon: 'RefreshCw',
        title: 'Instant Conversion',
        description: 'Convert JPG to PNG in seconds, right in your browser tab.',
      },
      {
        icon: 'ImageIcon',
        title: 'Lossless PNG Output',
        description: 'PNG is lossless — your converted image retains full quality.',
      },
      {
        icon: 'ShieldCheck',
        title: 'Private Processing',
        description: 'Images never leave your device — processed 100% locally.',
      },
      {
        icon: 'Globe',
        title: 'No Install Needed',
        description: 'Works in any modern browser on desktop or mobile.',
      },
      {
        icon: 'Layers',
        title: 'Transparency Support',
        description: 'PNG supports transparent backgrounds, unlike JPG.',
      },
      {
        icon: 'Sparkles',
        title: 'Completely Free',
        description: 'No account, no payment, no limits.',
      },
    ],
    steps: [
      {
        step: 1,
        title: 'Upload JPG File',
        description: 'Select your JPG or JPEG image from your device.',
      },
      {
        step: 2,
        title: 'Convert to PNG',
        description: 'FileMingle converts the image instantly in your browser.',
      },
      {
        step: 3,
        title: 'Download PNG',
        description: 'Save the high-quality PNG file to your device.',
      },
    ],
    featureDetails:
      'Converting JPG to PNG with FileMingle is as simple as uploading your file. PNG offers lossless compression and supports transparency — making it ideal for logos, graphics, and images that need a transparent background. The conversion preserves your original image dimensions and produces a clean, high-quality PNG.',
    faqs: [
      {
        q: 'Why would I convert JPG to PNG?',
        a: 'PNG supports lossless compression and transparent backgrounds, making it better for logos, icons, and graphics where quality or transparency matters.',
      },
      {
        q: 'Is JPG to PNG conversion free?',
        a: 'Yes, FileMingle\'s JPG to PNG converter is completely free.',
      },
      {
        q: 'Will converting JPG to PNG improve image quality?',
        a: 'Converting to PNG prevents further quality loss since PNG is lossless. However, any quality already lost in the original JPG cannot be recovered.',
      },
      {
        q: 'Do I need to create an account?',
        a: 'No account is needed. Just open the tool and start converting.',
      },
      {
        q: 'Are my JPG files uploaded to a server?',
        a: 'No. All conversion happens in your browser. Your files stay on your device.',
      },
      {
        q: 'Can I convert multiple JPG files at once?',
        a: 'Yes, FileMingle supports batch conversion so you can process multiple files in one session.',
      },
    ],
    relatedSlugs: ['image-to-png', 'png-compressor', 'compress-image'],
  },

  {
    slug: 'png-compressor',
    metaTitle: 'PNG Compressor Online | FileMingle',
    metaDescription:
      'Compress PNG images online and reduce file size while preserving image quality and transparency — free with FileMingle.',
    h1: 'Compress PNG Images Online',
    badge: 'PNG Compressor',
    description:
      'Reduce the size of your PNG files while preserving quality and transparency. Fast, browser-based PNG compression — no software needed.',
    trustMessage: 'Smaller PNGs, same great quality.',
    appUrl: `${APP_BASE}/compress-image`,
    primaryCta: 'Compress PNG Now',
    benefits: [
      {
        icon: 'Minimize2',
        title: 'Significant Size Reduction',
        description: 'Shrink PNG files dramatically without sacrificing visible quality.',
      },
      {
        icon: 'Layers',
        title: 'Transparency Preserved',
        description: 'PNG transparency (alpha channel) is maintained after compression.',
      },
      {
        icon: 'Sliders',
        title: 'Adjustable Compression',
        description: 'Choose your compression level to balance size and quality.',
      },
      {
        icon: 'ShieldCheck',
        title: 'Private & Local',
        description: 'Compression runs in your browser — files never leave your device.',
      },
      {
        icon: 'Zap',
        title: 'Fast Results',
        description: 'Compressed PNG files are ready in seconds.',
      },
      {
        icon: 'Globe',
        title: 'Works on Any Device',
        description: 'No installs — use it on desktop, tablet, or mobile.',
      },
    ],
    steps: [
      {
        step: 1,
        title: 'Upload Your PNG',
        description: 'Select the PNG file you want to compress.',
      },
      {
        step: 2,
        title: 'Set Compression Level',
        description: 'Choose how much to compress — from light to heavy.',
      },
      {
        step: 3,
        title: 'Download Smaller PNG',
        description: 'Save your compressed PNG — transparency included.',
      },
    ],
    featureDetails:
      'FileMingle\'s PNG compressor applies smart compression to reduce file size while keeping your image sharp and your transparency intact. It\'s ideal for web developers optimizing page load times, designers sharing assets, or anyone looking to reduce storage use without losing PNG\'s lossless benefits.',
    faqs: [
      {
        q: 'Does compressing a PNG remove transparency?',
        a: 'No. FileMingle preserves the PNG alpha channel (transparency) during compression.',
      },
      {
        q: 'How much can I reduce a PNG file size?',
        a: 'Depending on the image, you can often reduce PNG size by 30–70% without visible quality loss.',
      },
      {
        q: 'Is PNG compression free on FileMingle?',
        a: 'Yes, the PNG compressor is completely free to use.',
      },
      {
        q: 'Will compression make my PNG look worse?',
        a: 'Light compression is virtually invisible. Heavier compression may introduce minor artifacts. You control the level.',
      },
      {
        q: 'Are my PNG files sent to a server?',
        a: 'No. Compression is handled entirely in your browser. Your files never leave your device.',
      },
      {
        q: 'Can I compress PNG files on mobile?',
        a: 'Yes, FileMingle works on any smartphone or tablet with a modern browser.',
      },
    ],
    relatedSlugs: ['compress-image', 'image-to-png', 'jpg-to-png'],
  },
];

export const getToolBySlug = (slug) => tools.find((t) => t.slug === slug);

export const getRelatedTools = (slug) => {
  const tool = getToolBySlug(slug);
  if (!tool) return [];
  return tool.relatedSlugs.map((s) => getToolBySlug(s)).filter(Boolean);
};

export const allToolCards = [
  {
    slug: 'pdf-form-builder',
    label: 'PDF Form Builder',
    description: 'Build professional fillable PDF forms with drag-and-drop field placement.',
    icon: 'LayoutTemplate',
    color: 'from-violet-500 to-purple-600',
  },
  {
    slug: 'fillable-pdf-builder',
    label: 'Fillable PDF Builder',
    description: 'Turn any PDF into a fillable form with text fields, signatures, and more.',
    icon: 'FileText',
    color: 'from-blue-500 to-indigo-600',
  },
  {
    slug: 'image-to-png',
    label: 'Image to PNG Converter',
    description: 'Convert JPG, WebP, BMP, and other image formats to PNG in seconds.',
    icon: 'ImageIcon',
    color: 'from-sky-500 to-blue-600',
  },
  {
    slug: 'jpg-to-png',
    label: 'JPG to PNG Converter',
    description: 'Quickly convert JPG and JPEG images to lossless PNG format.',
    icon: 'RefreshCw',
    color: 'from-cyan-500 to-sky-600',
  },
  {
    slug: 'compress-image',
    label: 'Image Compressor',
    description: 'Reduce image file sizes while preserving visual quality — JPG & PNG.',
    icon: 'Minimize2',
    color: 'from-emerald-500 to-teal-600',
  },
  {
    slug: 'png-compressor',
    label: 'PNG Compressor',
    description: 'Compress PNG images online while keeping transparency intact.',
    icon: 'Layers',
    color: 'from-teal-500 to-green-600',
  },
];
